import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  ConnectionJobRowSchema,
  CrupdateSubscriptionSchema,
  ExecuteTimelyDataDiffMetaSchema,
  GenericSubscriptionWebhookId,
  GenericTimelyAccountId,
  GetIntegrationDetailsParamsSchema,
  GetIntegrationDetailsQuerySchema,
  GetNangoSessionTokenQuerySchema,
  GetNangoTokenParamsSchema,
  IntegrationDetailsSchema,
  IntegrationSchema,
  ListIntegrationsQuerySchema,
  mcpExposeMetadata,
  OKGeneric,
  PartialBatchUpdateSubscriptionsSchema,
  ProviderBatchOperationIdsSchema,
  ProviderIdentitySchema,
  ProviderIdSchema,
  ProviderImportOperationsSchema,
  // Keep ProviderKeySchema for other endpoints that still use it
  ProviderKeySchema,
  ProviderOperationIdsSchema,
  QueryBooleanSchema,
  QueryReturnedDataDiffSchema,
  ResponseMetaSchema,
  SubscribableResourceSchema,
  SyncRunProgressSchema,
  TimelyResourcesDataSchema,
  TimelySupportedResourceTypeEnumsSchema,
  URLParamsSchema,
} from '../models';

extendZodWithOpenApi(z);
const c = initContract();

const { accountId, connectionId, nangoConnectionId, subscriptionId, resourceId } = URLParamsSchema;

const OperationsLogSchema = z
  .object({
    message: z.string().openapi({ example: 'github/importOrganizations OK' }),
    status: z.string().openapi({ example: 'ok' }).default('ok').optional(),
    job_id: z.string().optional().describe('Unique ID for tracking operation execution'),
    errors: z
      .array(z.string())
      .optional()
      .openapi({ example: ['Error 1', 'Error 2'] }),
    jobSummary: z
      .object({
        executionSeconds: z.string().openapi({ example: '0.1' }),
        executedOperations: z
          .array(z.string().optional())
          .optional()
          .openapi({ example: ['importOrganizations', 'upsertBucketsAndAssociations'] }),

        okCount: z.number().openapi({ example: 2 }),
        errorCount: z.number().openapi({ example: 0 }),
        xhrLog: z.array(z.object({}).passthrough().optional()).optional(),
        connectionDetails: z.object({}).passthrough().optional(),
        storedDataCount: z.number().optional(),
      })
      .passthrough(),
    operationIdJobRuns: z
      .array(
        z
          .object({
            operationId: ProviderOperationIdsSchema.openapi({ example: 'importOrganizations' }),
            status: z.enum(['ok', 'error']).openapi({ example: 'ok' }).default('ok'),
            error: z.string().optional(),
            data: z.array(z.object({}).passthrough()).or(z.object({}).passthrough()).optional(),
          })
          .passthrough()
      )
      .optional(),
  })
  .passthrough();

export type OperationsLog = z.infer<typeof OperationsLogSchema>;

// Todo clean up shape of this vs API implementations, this can be more streamlined
const ProviderOperationsBody = z
  .object({
    message: z.string().openapi({ example: 'github/importOrganizations OK' }),
    status: z.string().openapi({ example: 'ok' }).default('ok'),
    operationsLog: OperationsLogSchema.nullable(),
    async: z
      .boolean()
      .optional()
      .openapi({ example: true, description: 'Indicates if operation runs in background' }),
    job_id: z.string().optional().openapi({
      example: 'conn-123-initialize-1648123456789',
      description: 'Unique ID for tracking operation execution',
    }),
    operation_execution_id: z.string().optional().openapi({
      example: 'conn-123-initialize-1648123456789',
      description: 'Unique ID for tracking background operation',
    }),
    trigger_run_id: z.string().optional().openapi({
      example: 'run_123abc',
      description: 'Background job identifier when executionMode=worker',
    }),
  })
  .passthrough();

/**
 * Paths
 */
export const apiIntegrations = c.router({
  updateBatchOfSubscriptions: {
    method: 'POST',
    path: `/1.0/integrations/:provider/config/subscriptions/batch/update`,
    description: `Update multiple subscriptions for a provider, and optionally kick off sync`,
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({ accountId, connectionId }),
    body: z.object({
      runOperationId: z.literal('synchronize').optional(), // keeping it simple atm..
      subscriptions: z.array(PartialBatchUpdateSubscriptionsSchema),
    }),
    responses: {
      200: ProviderOperationsBody.or(OKGeneric),
    },
  },
  crupdateSubscription: {
    method: 'POST',
    path: `/1.0/integrations/:provider/config/subscriptions`,
    description: `Creates or updates a subscription for a provider`,
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({
      accountId,
      connectionId: connectionId.optional(),
      subscriptionId: subscriptionId.optional(),
    }),
    body: CrupdateSubscriptionSchema,
    responses: {
      200: z.object({ subscription: SubscribableResourceSchema }),
    },
  },

  getSubscribableResources: {
    method: 'GET',
    path: `/1.0/integrations/:provider/config/subscriptions`,
    description: `Returns all subscriptions for a provider`,
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({ accountId, connectionId }),
    responses: {
      200: z.object({
        subscriptions: z.array(SubscribableResourceSchema),
        meta: ResponseMetaSchema.pick({ count: true, totalCount: true }),
      }),
    },
  },
  getSubscription: {
    method: 'GET',
    path: `/1.0/integrations/:provider/config/subscription`,
    description: `Returns a single subscription /w relevant information`,
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({
      accountId,
      subscriptionId: subscriptionId.optional(),
      resourceId,
    }),
    responses: {
      200: z.object({
        subscription: SubscribableResourceSchema,
      }),
    },
  },
  getIdentities: {
    method: 'GET',
    path: `/1.0/integrations/:provider/config/identities`,
    description: `Returns the authenticated users identity for a provider`,
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({ accountId, connectionId: connectionId.optional() }),
    responses: {
      200: z.array(ProviderIdentitySchema),
    },
  },
  getNangoSessionToken: {
    method: 'GET',
    path: `/1.0/integrations/:providerId/session_token`,
    pathParams: GetNangoTokenParamsSchema,
    query: GetNangoSessionTokenQuerySchema,
    responses: {
      200: z.object({ token: z.string() }),
    },
    summary: 'Generate a session token for Nango service interactions',
    description:
      'Generates a short-lived session token for authenticating with the Nango OAuth service. This token is used by the frontend to initiate OAuth flows for connecting third-party integrations. The token enables secure browser-based authentication without exposing sensitive credentials.',
  },
  providerImportDataType: {
    method: 'GET',
    path: `/1.0/integrations/:provider/import/:dataType`,
    description: 'A generic route/gateway for executing a specific `operationId` on a `provider`',
    pathParams: z.object({
      provider: ProviderKeySchema,
      dataType: z.string().describe('Data type to import'),
    }),
    query: z.object({ accountId, resourceId }),
    responses: {
      200: ProviderOperationsBody,
    },
  },
  providerAcceptIncomingWebhook: {
    method: 'POST',
    path: `/1.0/integrations/webhook/:provider`,
    description:
      'Accepts incoming webhook by loading `provider` and executing `handleIncomingWebhook` operation.',
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({ webhookId: GenericSubscriptionWebhookId }),
    body: z
      .object({})
      .passthrough()
      .openapi({ example: {}, description: 'The payload of the webhook' }),
    responses: {
      200: z
        .object({})
        .passthrough()
        .openapi({
          example: { success: true },
          description: 'Whichever payload the origin expects in return',
        }),
      // Webhook is no longer valid for this connection (e.g., disconnected). Providers should uninstall
      // and stop retrying. Body may be `null` (default) or an informational object.
      410: z.object({}).passthrough().nullable().openapi({
        example: null,
        description:
          'Webhook no longer valid for this connection (e.g., disconnected). Body may be null or an informational object.',
      }),
    },
  },
  providerOperationId: {
    method: 'GET',
    path: `/1.0/integrations/:provider/:operationId`,
    description:
      'A generic route/gateway for executing a specific `operationId` on a `provider`, or general batch operations like `synchronize`.',
    pathParams: z.object({
      provider: ProviderKeySchema,
      operationId: z
        .union([ProviderImportOperationsSchema, ProviderBatchOperationIdsSchema])
        .describe('Operation to execute'),
    }),
    query: z.object({
      connectionId,
      // NB: This doesn't totally make sense, since systemOperations don't _actually_ run through this endpoint,
      // but ref hack-ish `ProviderSettingsDebug.tsx` calling `providerOperationId` for `initialize` and `synchronize`,
      // which are _actually_ caught by separate handlers ^^. Re-visit this whole thing, but for now, type "safety".
      nangoConnectionId: nangoConnectionId.optional(),
      currentTimelyAccountId: GenericTimelyAccountId.optional().describe(
        'The primary Timely workspace account ID for proper Pusher event routing (required for individual connections)'
      ),
      executionMode: z
        .enum(['serverless', 'worker'])
        .default('worker')
        .optional()
        .describe('Execution mode override; worker enqueues a pgboss background job'),
      forceFullImport: QueryBooleanSchema.optional().describe(
        'When true, clear TIC-side connection and subscription metadata before synchronize so the provider reimports from a clean import cursor without deleting stored resources'
      ),
      generateDiffAfterSync: QueryBooleanSchema.optional().describe(
        'When true, after a successful synchronize run, finalize integration setup (idempotent) and enqueue the initial full Timely diff generation'
      ),
      urlOrPath: z
        .string()
        .optional()
        .describe('Optional operation param passthrough (e.g. /task/<id>)'),
    }),
    responses: {
      200: ProviderOperationsBody,
    },
  },
  getIntegrations: {
    method: 'GET',
    path: `/2.0/integrations/list`,
    description:
      'Returns all integrations with connection status for both personal and account scopes',
    query: ListIntegrationsQuerySchema,
    responses: {
      200: z.object({
        data: z.array(IntegrationSchema),
        meta: ResponseMetaSchema,
      }),
    },
    metadata: mcpExposeMetadata,
  },
  getIntegrationDetails: {
    method: 'GET',
    path: `/2.0/integrations/:providerId/details`,
    description:
      'Returns complete integration details for a specific provider including configuration, operations, and connection status',
    pathParams: GetIntegrationDetailsParamsSchema,
    query: GetIntegrationDetailsQuerySchema,
    responses: {
      200: z.object({
        data: IntegrationDetailsSchema,
        meta: ResponseMetaSchema,
      }),
    },
    metadata: mcpExposeMetadata,
  },
  finalizeIntegrationSetup: {
    method: 'POST',
    path: `/2.0/integrations/:provider/finalize-setup`,
    description:
      'Finalizes integration setup by marking setup as completed and triggering data dump',
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({
      connectionId,
    }),
    body: z.object({}),
    responses: {
      200: OKGeneric,
    },
  },
  getDiffPreview: {
    method: 'GET',
    path: `/2.0/integrations/:providerId/diff_preview`,
    description: 'Generate import diff preview for provider connections (always in dryRun mode)',
    pathParams: z.object({
      providerId: ProviderIdSchema,
    }),
    query: z.object({
      connectionId: connectionId,
      timelyAccountId: GenericTimelyAccountId.optional(),
      format: z
        .enum(['raw_data', 'timely_format'])
        .default('raw_data')
        .optional()
        .describe('Response format'),
      resourceTypes: z
        .array(TimelySupportedResourceTypeEnumsSchema)
        .optional()
        .describe(
          'Filter results to specific resource types. If not provided, all supported types are included.'
        ),
    }),
    responses: {
      200: z.object({
        data: z.union([
          QueryReturnedDataDiffSchema.describe('Raw database format (format=raw_data)'),
          TimelyResourcesDataSchema.describe('Structured Timely format (format=timely_format)'),
        ]),
        meta: ExecuteTimelyDataDiffMetaSchema,
      }),
    },
    summary: 'Generate preview of import diff for provider connection',
  },
  regenerateFullDiff: {
    method: 'POST',
    path: `/2.0/integrations/:provider/regenerate-full-diff`,
    description:
      'Regenerates a full data diff for a connection with ALL data (not incremental) and saves to database',
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({
      connectionId,
      resourceTypes: z
        .array(TimelySupportedResourceTypeEnumsSchema)
        .optional()
        .describe(
          'Filter regeneration to specific resource types. If omitted, all supported types are included.'
        ),
    }),
    body: z.object({}),
    responses: {
      202: z.object({
        message: z.string(),
        trigger_run_id: z.string(),
      }),
    },
    summary: 'Regenerate full diff with all data for connection',
  },
  getConnectionJobs: {
    method: 'GET',
    path: `/2.0/integrations/:provider/connections/:connectionId/jobs`,
    description:
      'Returns pg-boss job rows scoped to the given connection. Active/queued/retrying jobs are always included; when `includeRecent` is true, jobs completed within the last hour are also returned.',
    pathParams: z.object({ provider: ProviderIdSchema, connectionId }),
    query: z.object({
      includeRecent: QueryBooleanSchema.optional().describe(
        'When true, also return jobs completed within the last hour'
      ),
    }),
    responses: {
      200: z.object({
        data: z.array(ConnectionJobRowSchema),
      }),
    },
    summary: 'List pg-boss jobs for a connection',
  },
  getConnectionSyncRunProgress: {
    method: 'GET',
    path: `/2.0/integrations/:provider/connections/:connectionId/sync-run-progress`,
    description:
      'Returns the ACTIVE (running) fan-out sync_runs for the connection, newest first — empty for providers without fan-out runs. total = outstanding + ok_count + error_count and grows while a run discovers units; completed = ok_count + error_count.',
    pathParams: z.object({ provider: ProviderIdSchema, connectionId }),
    responses: {
      200: z.object({
        data: z.array(SyncRunProgressSchema),
      }),
    },
    summary: 'Active sync_run progress for a connection',
  },
  getConnectionSyncRuns: {
    method: 'GET',
    path: `/2.0/integrations/:provider/connections/:connectionId/sync-runs`,
    description:
      'Returns the LATEST fan-out sync_runs for the connection, newest first, REGARDLESS of status — unlike sync-run-progress (running only), this keeps returning the most recent runs once they finish (completed, completed_with_errors, failed), to feed a sync-status card. total = outstanding + ok_count + error_count; completed = ok_count + error_count.',
    pathParams: z.object({ provider: ProviderIdSchema, connectionId }),
    responses: {
      200: z.object({
        data: z.array(SyncRunProgressSchema),
      }),
    },
    summary: 'Latest sync_runs for a connection',
  },
  getDiffsList: {
    method: 'GET',
    path: `/2.0/integrations/:provider/diffs`,
    description: 'Get paginated list of data diffs for a connection',
    pathParams: z.object({ provider: ProviderIdSchema }),
    query: z.object({
      connectionId,
      page: z.coerce.number().int().min(1).default(1).optional().describe('Page number'),
      perPage: z.coerce
        .number()
        .int()
        .min(1)
        .max(100)
        .default(20)
        .optional()
        .describe('Results per page'),
    }),
    responses: {
      200: z.object({
        data: z.object({
          diffs: z.array(
            z.object({
              id: z.string().uuid(),
              generated_at: z.string(),
              status: z.enum(['created', 'in_progress', 'success', 'failed']),
              error: z.string().nullable(),
              failed_count: z.number().int(),
              timely_account_id: GenericTimelyAccountId.nullable(),
              processed_by_timely_at: z.string().nullable(),
              trust_generated_at: z.boolean(),
              resource_count: z.number().int(),
            })
          ),
          latestGeneratedAt: z.string().nullable(),
          totalDiffsCount: z.number().int(),
          pendingDiffsCount: z.number().int(),
          failedDiffsCount: z.number().int(),
        }),
        meta: ResponseMetaSchema.pick({ count: true, totalCount: true }).extend({
          page: z.number().int().min(1),
        }),
      }),
    },
    summary: 'List diffs for a connection',
  },
  getDiffDetails: {
    method: 'GET',
    path: `/2.0/integrations/:provider/diffs/:diffId`,
    description:
      'Get detailed information about a specific diff including data and processing report',
    pathParams: z.object({
      provider: ProviderIdSchema,
      diffId: z.string().uuid().describe('Diff identifier'),
    }),
    query: z.object({
      connectionId,
      format: z
        .enum(['raw_data', 'timely_format'])
        .default('timely_format')
        .optional()
        .describe('Response format'),
    }),
    responses: {
      200: z.object({
        id: z.string().uuid(),
        generated_at: z.string(),
        status: z.enum(['created', 'in_progress', 'success', 'failed']),
        error: z.string().nullable(),
        failed_count: z.number().int(),
        timely_account_id: GenericTimelyAccountId.nullable(),
        processed_by_timely_at: z.string().nullable(),
        data: z.union([
          QueryReturnedDataDiffSchema.describe('Raw database format (format=raw_data)'),
          TimelyResourcesDataSchema.describe('Structured Timely format (format=timely_format)'),
        ]),
        processed_output: z
          .object({
            diff_id: z.string().uuid(),
            timely_account_id: GenericTimelyAccountId,
            timely_job_id: z.number().int(),
            status: z.enum(['success', 'failed']),
            error: z.string().nullable(),
            failed_count: z.number().int(),
            data: z.array(z.object({}).passthrough()),
          })
          .nullable(),
      }),
    },
    summary: 'Get diff details by ID',
  },
});
